import type {
  PrivateSiteConfig,
  PrivateStationConfig,
} from '@compassdigital/sdk.typescript/interface/config';
import type { SitePrivateConfig, StationPrivateConfig } from '@centric-os/types';
import { isEmpty, isObjectLike } from 'lodash-es';

/**
 * The typed config routes keep keystore secrets the caller may not read apart from the rest of
 * the keystore, under `keystore.restricted`. The FE predates that split and expects every secret
 * in its own keystore slot, with an unreadable one appearing as its encrypted envelope.
 *
 * Rather than rework every consumer of a private config at once, the stores map between the
 * two shapes here: the legacy shape the FE still works with, and the one the typed routes
 * exchange. Once the FE reads `keystore.restricted` directly this layer goes away.
 */

export function toLegacySitePrivateConfig(config: PrivateSiteConfig): SitePrivateConfig {
  return toLegacy(config);
}

export function fromLegacySitePrivateConfig(legacy: SitePrivateConfig): PrivateSiteConfig {
  return fromLegacy(legacy);
}

export function toLegacyStationPrivateConfig(config: PrivateStationConfig): StationPrivateConfig {
  return toLegacy(config);
}

export function fromLegacyStationPrivateConfig(legacy: StationPrivateConfig): PrivateStationConfig {
  return fromLegacy(legacy);
}

interface PrivateConfigLike {
  keystore?: object;
}

/** An inline secret wins over a withheld one of the same name, matching the service's own rule. */
function toLegacy<Legacy extends PrivateConfigLike>(config: PrivateConfigLike): Legacy {
  if (!config.keystore) {
    return config as Legacy;
  }
  const { restricted, ...inline } = config.keystore as Keystore;
  return { ...config, keystore: { ...restricted, ...inline } } as Legacy;
}

function fromLegacy<Config extends PrivateConfigLike>(legacy: PrivateConfigLike): Config {
  if (!legacy.keystore) {
    return legacy as Config;
  }
  const inline: Keystore = {};
  const restricted: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(legacy.keystore)) {
    (isEnvelope(value) ? restricted : inline)[name] = value;
  }
  return {
    ...legacy,
    keystore: isEmpty(restricted) ? inline : { ...inline, restricted },
  } as Config;
}

type Keystore = Record<string, unknown> & { restricted?: Record<string, unknown> };

function isEnvelope(value: unknown): boolean {
  return isObjectLike(value) && (value as { is_encrypted?: unknown }).is_encrypted === true;
}
